import React, { useState, useEffect, useCallback } from "react";
import {
  HardDrive, TrendingUp, ShoppingCart, Check, Loader,
  Video, FileText, Image, MessageCircle, Award, Paintbrush,
  FolderArchive, Files, Package,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import "./storage.scss";

const tooltipStyle = {
  backgroundColor: "var(--card-bg, #fff)",
  border: "1px solid var(--border-primary, #e5e7eb)",
  borderRadius: "8px",
  fontSize: "12px",
  padding: "8px 12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  color: "var(--text-primary, #111827)",
};

const CATEGORY_META = {
  videos: { label: "Videos", icon: Video, color: "#2563eb" },
  recordings: { label: "Recordings", icon: Video, color: "#7c3aed" },
  materials: { label: "Materials", icon: FileText, color: "#059669" },
  thumbnails: { label: "Thumbnails", icon: Image, color: "#d97706" },
  assignments: { label: "Assignments", icon: FolderArchive, color: "#0891b2" },
  certificates: { label: "Certificates", icon: Award, color: "#e11d48" },
  chat: { label: "Chat Files", icon: MessageCircle, color: "#6366f1" },
  branding: { label: "Branding", icon: Paintbrush, color: "#f59e0b" },
  other: { label: "Other", icon: Files, color: "#94a3b8" },
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

/* ═══════════════════════════════════════════════════════════════════════════ */
const StoragePage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [buyingGB, setBuyingGB] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [usageRes, purchaseRes] = await Promise.all([
        apiClient.get("/storage/my-usage"),
        apiClient.get("/storage/purchase-history"),
      ]);
      setData(usageRes.data);
      setPurchases(purchaseRes.data.purchases || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Purchase Extra Storage ──
  const handlePurchase = async (option) => {
    setBuyingGB(option.gb);
    setPaymentLoading(true);
    setSuccessMsg("");

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Payment gateway failed to load.");

      const { data: orderData } = await apiClient.post("/storage/create-purchase-order", {
        storageGB: option.gb,
      });

      const user = getCurrentUser();
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Extra Storage",
        description: `${option.gb} GB additional storage`,
        order_id: orderData.orderId,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: { color: "#2563eb" },
        handler: async (response) => {
          try {
            const verifyRes = await apiClient.post("/storage/verify-purchase", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSuccessMsg(verifyRes.data.message || `Added ${option.gb} GB!`);
            fetchData(); // refresh
          } catch {
            setSuccessMsg("");
          }
          setPaymentLoading(false);
          setBuyingGB(null);
        },
        modal: {
          ondismiss: () => {
            setPaymentLoading(false);
            setBuyingGB(null);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch {
      setPaymentLoading(false);
      setBuyingGB(null);
    }
  };

  if (loading) {
    return (
      <div className="stg-page">
        <div className="stg-page__header">
          <HardDrive size={24} />
          <div>
            <h1 className="stg-page__title">Storage</h1>
            <p className="stg-page__desc">Manage your institute's storage usage</p>
          </div>
        </div>
        <div className="stg-page__loading"><div className="spinner-border" /></div>
      </div>
    );
  }

  const usage = data?.usage || {};
  const plan = data?.plan || {};
  const categories = data?.categories || {};
  const monthly = data?.monthly || [];
  const purchaseOptions = data?.purchaseOptions || [];

  // Donut data from categories
  const catEntries = Object.entries(categories)
    .filter(([, v]) => v.sizeBytes > 0)
    .map(([key, v]) => ({
      key,
      label: CATEGORY_META[key]?.label || key,
      icon: CATEGORY_META[key]?.icon || Files,
      color: CATEGORY_META[key]?.color || "#94a3b8",
      sizeGB: v.sizeGB,
      sizeBytes: v.sizeBytes,
      count: v.count,
    }))
    .sort((a, b) => b.sizeBytes - a.sizeBytes);

  const usagePercent = usage.usagePercent || 0;
  const isWarning = usagePercent >= 70 && usagePercent < 90;
  const isDanger = usagePercent >= 90;
  const barColor = isDanger ? "danger" : isWarning ? "warning" : "";

  return (
    <div className="stg-page">
      {/* Header */}
      <div className="stg-page__header">
        <div className="stg-page__header-icon">
          <HardDrive size={24} />
        </div>
        <div>
          <h1 className="stg-page__title">Storage</h1>
          <p className="stg-page__desc">
            {plan.name} plan — {plan.isUnlimited ? "Unlimited storage" : `${plan.storageGB} GB included`}
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMsg && (
        <div className="stg-success">
          <Check size={16} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Usage Overview Card */}
      <div className="stg-card stg-usage-card">
        <div className="stg-usage-card__top">
          <div className="stg-usage-card__meter">
            <div className="stg-usage-card__meter-header">
              <span className="stg-usage-card__used">{usage.usedGB || 0} GB used</span>
              <span className="stg-usage-card__total">
                {plan.isUnlimited ? "Unlimited" : `of ${usage.totalQuotaGB || 0} GB`}
              </span>
            </div>
            {!plan.isUnlimited && (
              <div className="stg-bar">
                <div className={`stg-bar__fill ${barColor}`} style={{ width: `${usagePercent}%` }} />
              </div>
            )}
            {isDanger && (
              <div className="stg-usage-card__warning">
                <span>Storage is almost full. Consider purchasing extra storage.</span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="stg-usage-card__stats">
            <div className="stg-usage-card__stat">
              <span className="stg-usage-card__stat-val">{usage.fileCount?.toLocaleString() || 0}</span>
              <span className="stg-usage-card__stat-lbl">Total Files</span>
            </div>
            <div className="stg-usage-card__stat">
              <span className="stg-usage-card__stat-val">{usage.remainingGB !== null ? `${usage.remainingGB} GB` : "∞"}</span>
              <span className="stg-usage-card__stat-lbl">Remaining</span>
            </div>
            <div className="stg-usage-card__stat">
              <span className="stg-usage-card__stat-val">{usage.extraStorageGB || 0} GB</span>
              <span className="stg-usage-card__stat-lbl">Extra Purchased</span>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="stg-grid-2">
        {/* Category Breakdown */}
        <div className="stg-card">
          <div className="stg-card__head">
            <div className="stg-card__title">Storage Breakdown</div>
          </div>
          <div className="stg-card__body">
            {catEntries.length > 0 ? (
              <>
                <div className="stg-donut-wrapper">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={catEntries} cx="50%" cy="50%" innerRadius={48} outerRadius={70} paddingAngle={3} dataKey="sizeGB" strokeWidth={0}>
                        {catEntries.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} GB`]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="stg-donut-center">
                    <span className="stg-donut-center__val">{usage.usedGB || 0}</span>
                    <span className="stg-donut-center__lbl">GB used</span>
                  </div>
                </div>
                <div className="stg-cat-list">
                  {catEntries.map((cat) => (
                    <div key={cat.key} className="stg-cat-row">
                      <div className="stg-cat-row__info">
                        <span className="stg-cat-row__dot" style={{ background: cat.color }} />
                        <cat.icon size={14} style={{ color: cat.color }} />
                        <span className="stg-cat-row__name">{cat.label}</span>
                      </div>
                      <div className="stg-cat-row__right">
                        <span className="stg-cat-row__count">{cat.count} files</span>
                        <span className="stg-cat-row__size">{cat.sizeGB} GB</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="stg-empty">No storage data yet</div>
            )}
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="stg-card">
          <div className="stg-card__head">
            <div className="stg-card__title">Monthly Activity</div>
            <TrendingUp size={16} className="stg-card__icon" />
          </div>
          <div className="stg-card__body">
            {monthly.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthly} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="stgGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" vertical={false} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis axisLine={false} tickLine={false} tickFormatter={(v) => `${v}GB`} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} GB`]} />
                  <Area type="monotone" dataKey="uploadedGB" name="Uploaded" stroke="#2563eb" strokeWidth={2} fill="url(#stgGrad)" dot={false} />
                  <Area type="monotone" dataKey="deletedGB" name="Deleted" stroke="#e11d48" strokeWidth={1.5} fill="none" dot={false} strokeDasharray="5 3" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="stg-empty stg-empty--tall">No monthly data yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Extra Storage Purchase */}
      {purchaseOptions.length > 0 && (
        <div className="stg-card">
          <div className="stg-card__head">
            <div>
              <div className="stg-card__title">Need More Storage?</div>
              <div className="stg-card__subtitle">Purchase additional storage for your institute</div>
            </div>
            <Package size={18} className="stg-card__icon" />
          </div>
          <div className="stg-card__body">
            <div className="stg-purchase-grid">
              {purchaseOptions.map((opt) => (
                <div key={opt.gb} className="stg-purchase-option">
                  <div className="stg-purchase-option__top">
                    <div className="stg-purchase-option__gb">{opt.gb} GB</div>
                    <div className="stg-purchase-option__price">₹{opt.price.toLocaleString("en-IN")}</div>
                    <div className="stg-purchase-option__unit">+ taxes as applicable</div>
                  </div>
                  <button
                    className="stg-purchase-option__btn"
                    onClick={() => handlePurchase(opt)}
                    disabled={paymentLoading}
                  >
                    {paymentLoading && buyingGB === opt.gb ? (
                      <><Loader size={14} className="stg-spin" /> Processing…</>
                    ) : (
                      <><ShoppingCart size={14} /> Buy Now</>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Purchase History */}
      {purchases.length > 0 && (
        <div className="stg-card">
          <div className="stg-card__head">
            <div className="stg-card__title">Purchase History</div>
          </div>
          <div className="stg-card__body stg-card__body--flush">
            <table className="stg-table">
              <thead>
                <tr>
                  <th>Storage</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p._id}>
                    <td><strong>{p.storageGB} GB</strong></td>
                    <td>₹{p.amountRupees}</td>
                    <td>
                      <span className={`stg-status stg-status--${p.status}`}>
                        {p.status === "paid" ? <Check size={12} /> : null}
                        {p.status}
                      </span>
                    </td>
                    <td>{new Date(p.activatedAt || p.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoragePage;
